import React, { useState, useCallback, useEffect } from 'react';
import {
  Drawer,
  Box,
  Paper,
  Tabs,
  Tab,
  Stack,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Typography,
  Snackbar,
  SelectChangeEvent,
  Tooltip,
} from '@mui/material';
import {
  Save as SaveIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  Send as SendIcon,
} from '@mui/icons-material';

// 🎯 Types and Constants
import type { OrderDetailDrawerProps, TemplateType } from './orderDetail/types';
import { TEMPLATE_OPTIONS } from './orderDetail/constants';
import { formatDate } from './orderDetail/utils';

// 🔧 Hooks
import { useOrderDetailForm } from './orderDetail/hooks/useOrderDetailForm';
import { useAlertManagement } from './orderDetail/hooks/useAlertManagement';
import { useDeviceManagement } from './orderDetail/hooks/useDeviceManagement';

// 🎨 Components
import OrderDetailHeader from './orderDetail/components/OrderDetailHeader';
import ProcessingForm from './orderDetail/components/ProcessingForm';
import DeviceProcessingList from './orderDetail/components/DeviceProcessingList';
import DetailInfoTab from './orderDetail/components/DetailInfoTab';
import AlertTab from './orderDetail/components/AlertTab';
import MemoTab from './orderDetail/components/MemoTab';
import AdminToast from './AdminToast';

const OrderDetailDrawer = ({
  open,
  onClose,
  application,
  adminId,
  onUpdate
}: OrderDetailDrawerProps) => {
  // 🗂️ 탭 관리
  const [tabIndex, setTabIndex] = useState(0);
  
  // 🔧 기기별 로컬 변경사항 관리
  const [deviceLocalChanges, setDeviceLocalChanges] = useState<{
    hasChanges: boolean;
    pendingChanges: Record<string, any>;
    saveFunction?: () => Promise<void>;
  }>({
    hasChanges: false,
    pendingChanges: {},
    saveFunction: undefined
  });

  // 🎯 폼 관리 훅
  const formHook = useOrderDetailForm(application, adminId, onUpdate, onClose);

  // 🔔 알림톡 관리 훅  
  const alertHook = useAlertManagement(application);

  // 🔄 기기별 관리 훅
  const deviceHook = useDeviceManagement(
    application, 
    onUpdate, 
    (error) => formHook.showToast(error, 'error')
  );

  // 🎯 Drawer 열릴 때 토스트 상태 초기화
  useEffect(() => {
    if (open) {
      formHook.hideToast();
    }
  }, [open, formHook.hideToast]);

  // 📋 계좌번호 복사 핸들러
  const handleCopyAccount = useCallback(() => {
    if (application?.bank_account) {
      navigator.clipboard.writeText(application.bank_account);
      formHook.showToast('계좌번호가 복사되었습니다', 'success');
    }
  }, [application, formHook.showToast]);

  // 🏠 주소 복사 핸들러
  const handleCopyAddress = useCallback(() => {
    if (application?.pickup_address) {
      const fullAddress = application.pickup_address_detail
        ? `${application.pickup_address} ${application.pickup_address_detail}`
        : application.pickup_address;
      navigator.clipboard.writeText(fullAddress);
      formHook.showToast('주소가 복사되었습니다', 'success');
    }
  }, [application, formHook.showToast]);

  // 🗂️ 탭 변경 핸들러
  const handleTabChange = useCallback((event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  }, []);

  // 📝 폼 데이터 변경 핸들러
  const handleFormDataChange = useCallback((data: Partial<typeof formHook.formData>) => {
    formHook.setFormData(prev => ({ ...prev, ...data }));
  }, [formHook.setFormData]);

  // 🔔 알림톡 핸들러들
  const handleAlertTemplateSelect = useCallback((e: SelectChangeEvent<string>) => {
    alertHook.handleTemplateSelect(e.target.value as TemplateType | '');
  }, [alertHook.handleTemplateSelect]);

  const handleAlertRepairReasonChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    alertHook.handleRepairReasonChange(e.target.value);
  }, [alertHook.handleRepairReasonChange]);

  const handleAlertReturnTrackingChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    alertHook.handleReturnTrackingChange(e.target.value);
  }, [alertHook.handleReturnTrackingChange]);

  // 🎨 TabPanel 컴포넌트 (최종 안정화)
  const TabPanel = React.useCallback(({ children, value, index }: {
    children: React.ReactNode;
    value: number;
    index: number
  }) => {
    if (value !== index) return null;
    return (
      <Box sx={{ pt: 2 }} key={`tab-content-${index}`}>
        {children}
      </Box>
    );
  }, []);

  // 🎨 로딩 상태
  if (!application) {
    return (
      <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: 520, maxWidth: '100vw' } }}>
        <Box sx={{ p: 3 }}>
          <Alert severity="warning">신청서 정보를 불러올 수 없습니다.</Alert>
          <Button variant="contained" onClick={onClose} fullWidth sx={{ mt: 2 }}>닫기</Button>
        </Box>
      </Drawer>
    );
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 520,
          maxWidth: '100vw',
          borderRadius: '16px 0 0 16px',
          bgcolor: '#fafbfc',
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* 🔒 고정 헤더 */}
      <OrderDetailHeader
        application={application}
        hasChanges={formHook.hasChanges}
        loading={formHook.loading}
        onClose={onClose}
        onCopyAccount={handleCopyAccount}
        onCopyAddress={handleCopyAddress}
      />

      {/* 📝 스크롤 가능한 컨텐츠 영역 */}
      <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
        {/* ⚙️ 처리 정보 - 최우선 표시 */}
        <ProcessingForm
          application={application}
          formData={formHook.formData}
          onFormDataChange={handleFormDataChange}
        />

        {/* 🔄 기기별 처리 - 새로운 기능 */}
        {application.devices && application.devices.length > 0 && (
          <DeviceProcessingList
            devices={application.devices}
            onDeviceUpdate={deviceHook.handleDeviceUpdate}
            onBatchDeviceUpdate={deviceHook.handleBatchDeviceUpdate}
            loading={deviceHook.loading}
            onLocalChanges={(hasChanges, pendingChanges, saveFunction) => {
              setDeviceLocalChanges({
                hasChanges,
                pendingChanges,
                saveFunction
              });
            }}
          />
        )}

        {/* 🗂️ 보조 기능 탭 구조 */}
        <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid #e0e7ef', bgcolor: '#fff' }}>
          {/* 탭 헤더 */}
          <Box sx={{ borderBottom: '1px solid #e0e7ef' }}>
            <Tabs
              value={tabIndex}
              onChange={handleTabChange}
              variant="fullWidth"
              sx={{
                minHeight: 48,
                '& .MuiTab-root': {
                  minHeight: 48,
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  color: '#666',
                  '&.Mui-selected': { color: '#1976d2' },
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: '#1976d2',
                  height: 3,
                },
              }}
            >
              <Tab label="📋 상세정보" />
              <Tab label="🔔 알림톡" />
              <Tab label="📝 메모" />
            </Tabs>
          </Box>

          {/* 탭 컨텐츠 */}
          <Box sx={{ p: 2 }}>
            {/* 📋 상세 정보 탭 */}
            <TabPanel value={tabIndex} index={0}>
              <DetailInfoTab
                application={application}
                onCopyAccount={handleCopyAccount}
                onCopyAddress={handleCopyAddress}
              />
            </TabPanel>

            {/* 🔔 알림톡 전송 탭 */}
            <TabPanel value={tabIndex} index={1}>
              <AlertTab
                application={application}
                alertState={alertHook.alertState}
                alertPreviewMessage={alertHook.alertPreviewMessage}
                isAlertFormValid={alertHook.isAlertFormValid}
                onTemplateSelect={handleAlertTemplateSelect}
                onRepairReasonChange={handleAlertRepairReasonChange}
                onReturnTrackingChange={handleAlertReturnTrackingChange}
                onSendClick={alertHook.handleSendClick}
                onConfirmSend={alertHook.handleConfirmSend}
                onSetConfirmDialog={alertHook.setConfirmDialog}
                onSetSnackbar={alertHook.setSnackbar}
              />
            </TabPanel>

            {/* 📝 관리자 메모 탭 */}
            <TabPanel value={tabIndex} index={2}>
              <MemoTab
                value={formHook.formData.remark}
                onChange={formHook.handleRemarkChange}
              />
            </TabPanel>
          </Box>
        </Paper>
      </Box>

      {/* 🔘 하단 액션 버튼 */}
      <Box sx={{ p: 3, borderTop: '1px solid #e0e7ef', bgcolor: '#fff' }}>
        <Stack direction="row" spacing={2}>
          <Button
            variant="contained"
            fullWidth
            disabled={formHook.loading || (!formHook.hasChanges && !deviceLocalChanges.hasChanges)}
            onClick={async () => {
              try {
                let hasAnyChanges = false;
                
                // 기기별 로컬 변경사항 먼저 저장
                if (deviceLocalChanges.hasChanges && deviceLocalChanges.saveFunction) {
                  await deviceLocalChanges.saveFunction();
                  hasAnyChanges = true;
                }
                
                // 그 다음 폼 데이터 저장 (기존 로직 사용하되 자동 닫기 방지)
                if (formHook.hasChanges) {
                  // handleSave는 자동으로 토스트와 닫기를 처리하므로 그대로 사용
                  await formHook.handleSave();
                  hasAnyChanges = true;
                } else if (hasAnyChanges) {
                  // 기기 변경사항만 있었던 경우 수동으로 토스트와 닫기 처리
                  formHook.showToast('신청서가 성공적으로 업데이트되었습니다', 'success');
                  
                  // 🎯 성공 후 Drawer 자동 닫기 (사용자가 메시지를 볼 수 있도록 딜레이)
                  if (onClose) {
                    setTimeout(() => {
                      formHook.hideToast(); // 토스트 먼저 숨기기
                      onClose();
                    }, 1200);
                  }
                }
              } catch (error) {
                console.error('Save failed:', error);
                formHook.showToast('저장에 실패했습니다.', 'error');
              }
            }}
            startIcon={formHook.loading ? <RefreshIcon className="rotating" /> : <SaveIcon />}
            sx={{ borderRadius: 2, py: 1.5, fontWeight: 600 }}
          >
            {formHook.loading ? '저장 중...' : '저장'}
          </Button>
          <Tooltip
            title={
              application.status === '완료' ? '완료된 신청건은 삭제할 수 없습니다' :
                '신청서 삭제'
            }
            arrow
          >
            <span>
              <Button
                variant="outlined"
                color="error"
                onClick={() => formHook.setConfirmOpen(true)}
                startIcon={<DeleteIcon />}
                disabled={application.status === '완료'}
                sx={{
                  borderRadius: 2,
                  py: 1.5,
                  fontWeight: 600,
                  minWidth: 100,
                  opacity: (application.status === '완료') ? 0.6 : 1,
                }}
              >
                삭제
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Box>

      {/* 🗑️ 삭제 확인 다이얼로그 */}
      <Dialog open={formHook.confirmOpen} onClose={() => formHook.setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
          <WarningIcon sx={{ mr: 1, color: 'error.main' }} />
          신청서 삭제 확인
        </DialogTitle>
        <DialogContent>
          {application.status === '완료' ? (
            <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
              <Typography variant="body1" gutterBottom>
                <strong>완료된 신청건은 삭제할 수 없습니다</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                완료 상태의 신청건은 중요한 기록으로 보존되어야 합니다.
              </Typography>
            </Alert>
          ) : (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
              <Typography variant="body1" gutterBottom>
                <strong>{application.user_name}</strong>님의 신청서를 삭제하시겠습니까?
              </Typography>
              <Typography variant="body2" color="text.secondary">
                삭제된 데이터는 복구할 수 없으며, 모든 관련 정보가 영구적으로 제거됩니다.
              </Typography>
            </Alert>
          )}
          <Typography variant="body2" color="text.secondary">
            신청번호: {application.application_number || application.id?.slice(-8) || 'N/A'}<br />
            신청일: {formatDate(application.created_at)}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={() => formHook.setConfirmOpen(false)}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            취소
          </Button>
          <Button
            onClick={() => formHook.handleDelete(onClose)}
            color="error"
            variant="contained"
            disabled={formHook.loading || application.status === '완료'}
            startIcon={formHook.loading ? <RefreshIcon className="rotating" /> : <DeleteIcon />}
            sx={{ borderRadius: 2 }}
          >
            {formHook.loading ? '삭제 중...' : (application.status === '완료') ? '삭제 불가' : '삭제'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 토스트 알림 */}
      <AdminToast
        open={formHook.toast.open}
        message={formHook.toast.message}
        severity={formHook.toast.severity}
        onClose={formHook.hideToast}
      />

      {/* 🎨 CSS 애니메이션 */}
      <style>
        {`
            @keyframes rotate {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
            .rotating {
            animation: rotate 2s linear infinite;
            }
          `}
      </style>
    </Drawer>
  );
};

export default OrderDetailDrawer;